import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../hooks/useAuth";
import { toast, ToastContainer } from "react-toastify";
import { formatDateToDDMMYYYY } from "../components/FormatedDate";

export default function BadgeCycleForm() {
     const navigate = useNavigate();
     const [branches, setBranches] = useState([]);
     const [allTechnicians, setAllTechnicians] = useState([]);
     const [badgeCycles, setBadgeCycles] = useState([]);
     const [selectedBranchId, setSelectedBranchId] = useState("");
     const [year, setYear] = useState(new Date().getFullYear());
     const [quarter, setQuarter] = useState("Q4");
     const [technicianCycles, setTechnicianCycles] = useState({});
     const [statusUpdates, setStatusUpdates] = useState({});
     const [loading, setLoading] = useState(false);
     const token = sessionStorage.getItem("token");

     const quarters = [
          { value: "Q1", label: "Jan–Mar" },
          { value: "Q2", label: "Apr–Jun" },
          { value: "Q3", label: "Jul–Sep" },
          { value: "Q4", label: "Oct–Dec" },
     ];

     const statusOptions = [
          "ISSUED",
          "RETURNED",
          "SENT_TO_VENDOR",
          "ANALYZED"
     ];

     const quarterToPeriod = {
          Q1: "Jan–Mar",
          Q2: "Apr–Jun",
          Q3: "Jul–Sep",
          Q4: "Oct–Dec",
     };

     useEffect(() => {
          fetchBranches();
          fetchTechnicians();
     }, []);

     // Fetch badge cycles when branch/year/quarter changes
     useEffect(() => {
          if (selectedBranchId && year && quarter) {
               fetchBadgeCycles();
          } else {
               setBadgeCycles([]);
               setStatusUpdates({});
          }
     }, [selectedBranchId, year, quarter]);

     const fetchBranches = async () => {
          try {
               const res = await fetch(getApiUrl("/getBranches"), {
                    headers: { Authorization: `Bearer ${token}` },
               });
               const data = await res.json();
               setBranches(Array.isArray(data.data) ? data.data : []);
          } catch (err) {
               console.error("Error fetching branches:", err);
          }
     };

     const fetchTechnicians = async () => {
          try {
               const res = await fetch(getApiUrl("/getTechnicians"), {
                    headers: { Authorization: `Bearer ${token}` },
               });
               const data = await res.json();
               console.log("All technicians:", data.data);
               setAllTechnicians(Array.isArray(data.data) ? data.data : []);
          } catch (err) {
               console.error("Error fetching technicians:", err);
          }
     };

     const fetchBadgeCycles = async () => {
          try {
               setLoading(true);
               const res = await fetch(getApiUrl(`/getBadgeCycles?branch_id=${selectedBranchId}&year=${year}&quarter=${quarter}`), {
                    headers: { Authorization: `Bearer ${token}` },
               });
               const data = await res.json();
               const cycles = Array.isArray(data.data) ? data.data : [];
               setBadgeCycles(cycles);

               // Set initial status updates
               const statusMap = {};
               cycles.forEach(cycle => {
                    statusMap[cycle.cycle_id] = cycle.status;
               });
               setStatusUpdates(statusMap);
          } catch (err) {
               console.error("Error fetching badge cycles:", err);
               setBadgeCycles([]);
          } finally {
               setLoading(false);
          }
     };

     const filteredTechnicians = allTechnicians.filter(
          (tech) => String(tech.branch_id) === String(selectedBranchId)
     );

     console.log("Filtered technicians:", filteredTechnicians);

     // Check if technician already has a cycle
     const getCycleForTechnician = (techni_id) =>
          badgeCycles.find((c) => String(c.techni_id) === String(techni_id));

     // Get new cycles to create (technicians without existing cycles)
     const newCycles = filteredTechnicians
          .filter((tech) => !getCycleForTechnician(tech.techni_id))
          .map((tech) => ({
               badge_id: tech.badge_id || "",
               badge_code: tech.badge_code || "",
               techni_id: tech.techni_id,
               tech_name: tech.name,
          }));

     // Handle status change for existing cycles
     const handleStatusChange = (cycle_id, newStatus) => {
          setStatusUpdates((prev) => ({
               ...prev,
               [cycle_id]: newStatus,
          }));
     };

     // Handle status change for new technician cycles
     const handleNewTechnicianStatusChange = (technicianId, status) => {
          setTechnicianCycles({
               ...technicianCycles,
               [technicianId]: {
                    ...technicianCycles[technicianId],
                    status: status
               }
          });
     };

     // Handle form submission for creating new cycles
     const handleCreateCycles = async (e) => {
          e.preventDefault();
          if (!selectedBranchId) {
               toast.error("Please select a branch");
               return;
          }
          if (newCycles.length === 0) {
               toast.info("All technicians already have cycles for this quarter.");
               return;
          }
          // if (newCycles.some((c) => !c.badge_id)) {
          //      toast.error("Some technicians do not have a badge assigned.");
          //      return;
          // }

          setLoading(true);
          try {
               const payload = {
                    cycles: newCycles.map((c) => ({
                         badge_id: c.badge_id || null, // send null if missing
                         techni_id: c.techni_id,
                         branch_id: selectedBranchId,
                         year,
                         quarter,
                         cycle_period: quarterToPeriod[quarter],
                         status: technicianCycles[c.techni_id]?.status || "ISSUED",
                    })),
               };

               const res = await fetch(getApiUrl("/badge_cycles/bulk"), {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                         Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
               });

               if (!res.ok) throw new Error("Failed to add badge cycles");
               toast.success("Badge cycles created!");
               setTimeout(() => navigate("/badge-cycles"), 1500);
          } catch (err) {
               console.error("Error creating cycles:", err);
               toast.error("Failed to add badge cycles");
          } finally {
               setLoading(false);
          }
     };

     // Handle updating existing cycle statuses
     const handleUpdateStatuses = async (e) => {
          e.preventDefault();
          const updates = badgeCycles
               .filter((cycle) => statusUpdates[cycle.cycle_id] && statusUpdates[cycle.cycle_id] !== cycle.status)
               .map((cycle) => ({
                    cycle_id: cycle.cycle_id,
                    status: statusUpdates[cycle.cycle_id],
               }));

          if (updates.length === 0) {
               toast.info("No status changes to update.");
               return;
          }

          setLoading(true);
          try {
               const res = await fetch(getApiUrl("/badge_cycles/update-status-bulk"), {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                         Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ updates }),
               });

               if (!res.ok) throw new Error("Failed to update statuses");
               toast.success("Statuses updated!");
               fetchBadgeCycles(); // Refresh the data
          } catch (err) {
               console.error("Error updating statuses:", err);
               toast.error("Failed to update statuses");
          } finally {
               setLoading(false);
          }
     };

     return (
          <div style={{ padding: "2rem" }}>
               <ToastContainer />
               <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="h4">Add / Edit Badge Cycles (By selecting Branch)</h2>
                    <button
                         onClick={() => navigate("/badge-cycles")}
                         className="btn btn-secondary"
                    >
                         Back to Badge Cycles
                    </button>
               </div>
               <form onSubmit={newCycles.length > 0 ? handleCreateCycles : handleUpdateStatuses}>
                    <div className="card shadow">
                         <div className="card-body">
                              <div className="row mb-4">
                                   <div className="col-md-4">
                                        <label>Branch <span className="text-danger">*</span></label>
                                        <select
                                             className="form-control"
                                             value={selectedBranchId}
                                             onChange={(e) => setSelectedBranchId(e.target.value)}
                                             required
                                        >
                                             <option value="">Select Branch</option>
                                             {branches.map((b) => (
                                                  <option key={b.branch_id} value={b.branch_id}>
                                                       {b.branch_name}
                                                  </option>
                                             ))}
                                        </select>
                                   </div>
                                   <div className="col-md-4">
                                        <label>Year <span className="text-danger">*</span></label>
                                        <input
                                             type="number"
                                             className="form-control"
                                             value={year}
                                             onChange={(e) => setYear(e.target.value)}
                                             min="2020"
                                             max="2100"
                                             required
                                        />
                                   </div>
                                   <div className="col-md-4">
                                        <label>Quarter <span className="text-danger">*</span></label>
                                        <select
                                             className="form-control"
                                             value={quarter}
                                             onChange={(e) => setQuarter(e.target.value)}
                                             required
                                        >
                                             {quarters.map((q) => (
                                                  <option key={q.value} value={q.value}>
                                                       {q.label}
                                                  </option>
                                             ))}
                                        </select>
                                   </div>
                              </div>

                              <h5>Technicians in Branch</h5>
                              {!selectedBranchId ? (
                                   <p className="text-muted">Please select a branch first.</p>
                              ) : loading ? (
                                   <div>Loading...</div>
                              ) : (
                                   <table className="table table-bordered">
                                        <thead>
                                             <tr>
                                                  <th>Technician</th>
                                                  <th>Badge Code</th>
                                                  <th>Status</th>
                                                  <th>Issued Info</th>
                                                  <th>Returned Info</th>
                                                  <th>Received Info</th>
                                                  <th>Processed Info</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {badgeCycles.length > 0 ? (
                                                  badgeCycles.map((cycle) => (
                                                       <tr key={cycle.cycle_id}>
                                                            <td>{cycle.technician_name}</td>
                                                            <td>{cycle.badge_code}</td>
                                                            <td>
                                                                 <select
                                                                      className="form-control form-control-sm"
                                                                      value={statusUpdates[cycle.cycle_id] || cycle.status}
                                                                      onChange={(e) => handleStatusChange(cycle.cycle_id, e.target.value)}
                                                                 >
                                                                      {statusOptions.map((s) => (
                                                                           <option key={s} value={s}>{s}</option>
                                                                      ))}
                                                                 </select>
                                                            </td>
                                                            <td>
                                                                 {cycle.issued_by ? (
                                                                      <>
                                                                           Issued by: {cycle.issued_by}<br />
                                                                           On: {cycle.issued_at ? formatDateToDDMMYYYY(cycle.issued_at) : "--"}
                                                                      </>
                                                                 ) : "--"}
                                                            </td>
                                                            <td>
                                                                 {cycle.returned_at ? (
                                                                      <>
                                                                           Returned by: {cycle.returned_by}<br />
                                                                           On: {formatDateToDDMMYYYY(cycle.returned_at)}
                                                                      </>
                                                                 ) : "--"}
                                                            </td>
                                                            <td>
                                                                 {cycle.received_at ? (
                                                                      <>
                                                                           Received by: {cycle.received_by}<br />
                                                                           On: {formatDateToDDMMYYYY(cycle.received_at)}
                                                                      </>
                                                                 ) : "--"}
                                                            </td>
                                                            <td>
                                                                 {cycle.processed_date ? (
                                                                      <>
                                                                           Processed by: {cycle.processed_by}<br />
                                                                           On: {formatDateToDDMMYYYY(cycle.processed_date)}
                                                                      </>
                                                                 ) : "--"}
                                                            </td>
                                                       </tr>
                                                  ))
                                             ) : newCycles.length > 0 ? (
                                                  newCycles.map((newCycle) => (
                                                       <tr key={newCycle.techni_id}>
                                                            <td>{newCycle.tech_name}</td>
                                                            <td>{newCycle.badge_code || "No Badge Assigned"}</td>
                                                            <td>
                                                                 <select
                                                                      className="form-control form-control-sm"
                                                                      value={technicianCycles[newCycle.techni_id]?.status || "ISSUED"}
                                                                      onChange={(e) => handleNewTechnicianStatusChange(newCycle.techni_id, e.target.value)}
                                                                 >
                                                                      <option value="ISSUED">ISSUED</option>
                                                                      <option value="RETURNED">RETURNED</option>
                                                                 </select>
                                                            </td>
                                                            <td>Will be set on creation</td>
                                                            <td>--</td>
                                                            <td>--</td>
                                                            <td>--</td>
                                                       </tr>
                                                  ))
                                             ) : (
                                                  <tr>
                                                       <td colSpan={7} className="text-center">No technicians found for this branch.</td>
                                                  </tr>
                                             )}
                                        </tbody>
                                   </table>
                              )}

                              {selectedBranchId && newCycles.length > 0 && (
                                   <button
                                        type="submit"
                                        className="btn btn-primary mt-3"
                                        disabled={loading}
                                   >
                                        {loading ? (
                                             <>
                                                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                  Creating...
                                             </>
                                        ) : (
                                             "Create New Cycles"
                                        )}
                                   </button>
                              )}

                              {selectedBranchId && newCycles.length === 0 && badgeCycles.length > 0 && (
                                   <button
                                        type="submit"
                                        className="btn btn-success mt-3"
                                        disabled={loading}
                                   >
                                        {loading ? (
                                             <>
                                                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                  Updating...
                                             </>
                                        ) : (
                                             "Update Status"
                                        )}
                                   </button>
                              )}
                         </div>
                    </div>
               </form>
          </div>
     );
}